import type { ComponentProps } from 'react';
import { cn } from '@resource-kit/ui';

/**
 * `PageToolbar` — the control region for `PageHeader`'s `actions` slot.
 *
 * `PageHeader` already lays that slot out as `flex flex-wrap gap-2`, which is
 * enough for the common case of one or two buttons handed to it directly. It
 * stops being enough the moment a page wraps its controls in a container of
 * its own: the header then has ONE child to wrap, and if that child cannot
 * wrap internally it is a single max-content block that pins the row wider
 * than the viewport. A page with eight controls (the dispatch console: two
 * date presets, a date picker, reconcile, a consolidate toggle, a progress
 * bar, run, and two jump links) ran off the right edge of every phone that
 * way.
 *
 * So this is the container done right, and it exists so the correct thing is
 * also the available thing. Reach for it whenever a page hands `actions` more
 * than a couple of bare controls.
 *
 * Grouping is carried by the gap difference alone — `gap-x-3` between groups
 * against `gap-2` within one. No dividers and no borders: a header row is
 * chrome, and rules between control clusters read as structure the page does
 * not actually have.
 */
export function PageToolbar({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-toolbar"
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
      {...props}
    />
  );
}

/**
 * One cluster of related controls inside a `PageToolbar` — a date range, a
 * run-and-its-toggle, a set of jump links.
 *
 * It wraps internally as well, and that is deliberate rather than redundant:
 * a group is a hint about reading order, never a promise to stay on one line.
 * A group that refused to break would reintroduce exactly the max-content
 * block this component exists to remove, just one level down — the work-date
 * cluster alone is two buttons plus a 176px date picker, which is already most
 * of a phone's content column.
 */
export function PageToolbarGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-toolbar-group"
      className={cn('flex flex-wrap items-center gap-2', className)}
      {...props}
    />
  );
}
